import { CloudCustomerAddress } from "@omni/types/api/customer/cloud/address.model";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";

import { AddressValidationResult } from "@/components/Address/AddressValidation/hooks/useAddressForm.hook";
import { APIStatus as AddressAPIStatus } from "@/redux/modules/api/data/address/address.module";
import { CreateAddress } from "@/redux/modules/api/data/address/address.types";
import { TApiAction, TExtendedApiAction } from "@/redux/store.types";
import { AddressToValidate } from "@/utils/address/validation.types";

export type AddressToCreate = {
    type: AddressType;
    isBpostField: boolean;
    address: CreateAddress;
};

/** Either the created address, or the id of an address that was saved through the edit/create modal. */
export type AddressesByType = Partial<
    Record<AddressType, CloudCustomerAddress | string>
>;

/**
 * Addresses the user corrected through the edit/create modal already exist in Hybris,
 * so they are mapped by id. The others still need to be created with their new validation status.
 */
export const splitValidatedAddresses = (
    addresses: Array<AddressToCreate>,
    results: Array<AddressValidationResult>
) => {
    const byType: AddressesByType = {};
    const toCreate: Array<AddressToCreate> = [];

    addresses.forEach((entry, i) => {
        const { validationStatus, addressId } = results[i];
        if (addressId) {
            byType[entry.type] = addressId;
        } else {
            toCreate.push({
                ...entry,
                address: { ...entry.address, validationStatus }
            });
        }
    });

    return { byType, toCreate };
};

export type CreateAddressesAction = TApiAction<
    TExtendedApiAction<{ addresses: Array<CloudCustomerAddress> }>
>;

export type CreateCheckoutAddressesResult = {
    type: string;
    payload: {
        addresses: AddressesByType;
        message?: string;
    };
};

/**
 * Validates the new checkout addresses first and only then creates them, so they are stored with the
 * status the user ended up with. Addresses the user corrected through the edit/create modal already
 * exist and are not created again. A rejection from `validate` (e.g. the user closed the modal) stops
 * the flow before anything is created.
 */
export const createCheckoutAddresses = async ({
    addresses,
    validate,
    create
}: {
    addresses: Array<AddressToCreate>;
    validate: (
        addresses: Array<AddressToValidate>
    ) => Promise<Array<AddressValidationResult>>;
    create: (addresses: Array<CreateAddress>) => Promise<CreateAddressesAction>;
}): Promise<CreateCheckoutAddressesResult> => {
    if (addresses.length === 0) {
        return { type: "", payload: { addresses: {} } };
    }

    const results = await validate(
        addresses.map(({ type, isBpostField, address }) => ({
            ...address,
            isBpostField,
            currentStatus:
                address.validationStatus || AddressValidationStatus.Unknown,
            type
        }))
    );

    const { byType, toCreate } = splitValidatedAddresses(addresses, results);

    if (toCreate.length === 0) {
        return { type: "", payload: { addresses: byType } };
    }

    const action = await create(toCreate.map(({ address }) => address));

    if (action.type !== AddressAPIStatus.FAILURE) {
        toCreate.forEach(({ type }, i) => {
            byType[type] = action.payload.addresses[i];
        });
    }

    return {
        type: action.type,
        payload: {
            addresses: byType,
            message: action.payload.message
        }
    };
};

/**
 * Resolves the addresses sent to the checkout call. The payment address only falls back to the delivery
 * address when the cart has home delivery, a click & collect only cart has no delivery address to fall back to.
 */
export const resolveCheckoutAddresses = <T>({
    isHomeDelivery,
    samePaymentAddress,
    deliveryAddress,
    deliveryAddressId,
    paymentAddress,
    paymentAddressId
}: {
    isHomeDelivery: boolean;
    samePaymentAddress: boolean;
    deliveryAddress: T | undefined;
    deliveryAddressId: string | null | undefined;
    paymentAddress: T | undefined;
    paymentAddressId: string | null | undefined;
}) => {
    const isSamePaymentAddress =
        isHomeDelivery &&
        (samePaymentAddress || deliveryAddressId === paymentAddressId);

    return {
        deliveryAddress,
        deliveryAddressId,
        paymentAddress: isSamePaymentAddress ? deliveryAddress : paymentAddress,
        paymentAddressId: isSamePaymentAddress
            ? deliveryAddressId
            : paymentAddressId
    };
};

export const resolveCreatedAddress = (
    created: AddressesByType,
    addressType: AddressType,
    existingId: string | null
): { id: string | null; address: CloudCustomerAddress | undefined } => {
    if (existingId) {
        return { id: existingId, address: undefined };
    }

    const address = created[addressType];
    if (typeof address === "string") {
        return { id: address, address: undefined };
    }

    return { id: null, address };
};

/** Maps the created addresses back to delivery / payment; an existing address book id always wins. */
export const resolveCreatedAddresses = (
    created: AddressesByType,
    existingIds: {
        deliveryAddressId: string | null;
        paymentAddressId: string | null;
    }
) => {
    const delivery = resolveCreatedAddress(
        created,
        AddressType.Delivery,
        existingIds.deliveryAddressId
    );
    const payment = resolveCreatedAddress(
        created,
        AddressType.Payment,
        existingIds.paymentAddressId
    );

    return {
        deliveryAddressId: delivery.id,
        deliveryAddress: delivery.address,
        paymentAddressId: payment.id,
        paymentAddress: payment.address
    };
};
